<template>
    <checkable-field type="radio" class="list-select" hide-input required :name="name" :selected="selected" @updated="select">
        <template v-for="slot of slots" #[slot]>
            <slot :name="slot" />

            <check-icon v-if="selected.includes(slot) || value.includes(slot)" />
            <caret-icon v-if="caret && !selected.includes(slot) && !value.includes(slot)" :direction="caret" />
        </template>
    </checkable-field>
</template>

<script lang="ts" setup>
import { EmitEvents } from '@btcdirect/shared/src/components/types';
import { CheckableField, ValidatedStringArrayFieldData } from 'out-of-the-vue';
import { useSlots, ref } from 'vue';
import CaretIcon from '../icons/caret.vue';
import CheckIcon from '../icons/check.vue';

const emit = defineEmits<{ (event: 'updated', data: ValidatedStringArrayFieldData): void }>();

const props = defineProps({
    name: {
        type: String,
        required: true
    },
    selected: {
        type: Array as () => string[],
        required: false,
        default: []
    },
    caret: {
        type: String as () => 'right' | 'down',
        required: false
    }
});

const value = ref<string[]>(props.selected || []);

const select = (data: ValidatedStringArrayFieldData): void => {
    value.value = data.value;
    emit(EmitEvents.UPDATED, data);
};

const slots = Object.keys(useSlots());
</script>

<style lang="scss" scoped>
@use '@@scss/flex';
@use '@@scss/transitions';

.list-select {
    padding-bottom: 0.5em;

    &:not(.border) {
        :deep(.checkable-field-item) {
            &:not(:hover):not(.selected) {
                border-color: var(--ootv-list-select-background-color);
            }

            &:hover,
            &.selected {
                border-color: var(--ootv-list-select-hover-background-color);
            }
        }
    }

    &.border {
        :deep(.checkable-field-item) {
            border-color: var(--ootv-input-border-color);
        }
    }

    :deep(.checkable-field-item) {
        @include transitions.transition((background-color, border-color));
        border: 1px solid transparent;
        border-radius: 0.5em;
        box-sizing: content-box;
        color: var(--ootv-input-label-color);
        padding-left: 1em;
        padding-right: 1em;

        &:not(:hover):not(.selected) {
            background-color: var(--ootv-list-select-background-color);
        }

        &:hover,
        &.selected {
            background-color: var(--ootv-list-select-hover-background-color);
        }

        .content {
            @include flex.flex;
            @include flex.align(center, space-between);
            @include flex.resize(1);

            .check {
                width: 1em;
                color: var(--btcdirect-blue);
            }
        }

        svg {
            color: var(--text-color-light);
        }

        .icon {
            width: 2em;
        }
    }
}
</style>
